import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarIcon, Send, ShieldCheck, Clock, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "AC Install / Repair",
  "Heating / Furnace",
  "Heat Pump",
  "Indoor Air Quality",
  "Preventive Maintenance",
  "Commercial HVAC",
  "Other",
];

const TIME_OPTIONS = [
  "Morning (8am – 12pm)",
  "Afternoon (12pm – 5pm)",
  "First available",
];

export default function Booking() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service_type: "",
    preferred_time: "",
    notes: "",
    is_emergency: false,
  });
  const [date, setDate] = useState();
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.address || !form.service_type || !date) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_date: format(date, "yyyy-MM-dd"),
      };
      await axios.post(`${API}/bookings`, payload);
      toast.success("Appointment request received! We'll be in touch shortly.", {
        description: "A confirmation has been sent to your email.",
      });
      setForm({
        name: "",
        phone: "",
        email: "",
        address: "",
        service_type: "",
        preferred_time: "",
        notes: "",
        is_emergency: false,
      });
      setDate(undefined);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please call us at 785-288-0330.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="py-20 sm:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#E31837] mb-3">
              Book an Appointment
            </div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
              Schedule service in under a minute.
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              Tell us about your comfort issue. We'll confirm your appointment and dispatch a
              certified Lennox technician to your door.
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <Clock className="w-5 h-5 text-[#E31837] mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-900">Business hours</div>
                  <div className="text-sm text-slate-600">Mon – Fri · 8:00 AM – 5:00 PM</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <Phone className="w-5 h-5 text-[#E31837] mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-900">Need help now?</div>
                  <a
                    href="tel:7852880330"
                    data-testid="booking-call-link"
                    className="text-sm text-slate-600 hover:text-[#E31837]"
                  >
                    Call 785-288-0330 — 24/7 emergency line
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <ShieldCheck className="w-5 h-5 text-[#E31837] mt-0.5" />
                <div>
                  <div className="font-semibold text-slate-900">No-obligation quotes</div>
                  <div className="text-sm text-slate-600">Honest pricing, no high-pressure sales.</div>
                </div>
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="booking-form"
            className="lg:col-span-3 bg-slate-50 rounded-3xl p-6 sm:p-10 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-sm font-semibold text-slate-700">Full name *</Label>
                <Input
                  id="name"
                  data-testid="input-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="mt-2 h-12 rounded-xl bg-white border-slate-200 focus-visible:ring-[#0F172A]"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-semibold text-slate-700">Phone *</Label>
                <Input
                  id="phone"
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="mt-2 h-12 rounded-xl bg-white border-slate-200 focus-visible:ring-[#0F172A]"
                  placeholder="785-555-0000"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="input-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="mt-2 h-12 rounded-xl bg-white border-slate-200 focus-visible:ring-[#0F172A]"
                  placeholder="you@example.com"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="address" className="text-sm font-semibold text-slate-700">Service address *</Label>
                <Input
                  id="address"
                  data-testid="input-address"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  className="mt-2 h-12 rounded-xl bg-white border-slate-200 focus-visible:ring-[#0F172A]"
                  placeholder="123 Main St, Hiawatha, KS"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Service type *</Label>
                <Select
                  value={form.service_type}
                  onValueChange={(v) => update("service_type", v)}
                >
                  <SelectTrigger
                    data-testid="select-service"
                    className="mt-2 h-12 rounded-xl bg-white border-slate-200"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-opt-${s.slice(0,4).toLowerCase()}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-700">Preferred date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="date-picker-trigger"
                      className="mt-2 h-12 w-full justify-start rounded-xl bg-white border-slate-200 font-normal text-slate-700 hover:bg-white"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm font-semibold text-slate-700">Preferred time</Label>
                <Select
                  value={form.preferred_time}
                  onValueChange={(v) => update("preferred_time", v)}
                >
                  <SelectTrigger
                    data-testid="select-time"
                    className="mt-2 h-12 rounded-xl bg-white border-slate-200"
                  >
                    <SelectValue placeholder="Any time works" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_OPTIONS.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="notes" className="text-sm font-semibold text-slate-700">
                  Tell us what's going on (optional)
                </Label>
                <Textarea
                  id="notes"
                  data-testid="input-notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  className="mt-2 rounded-xl bg-white border-slate-200 min-h-[110px] focus-visible:ring-[#0F172A]"
                  placeholder="e.g. AC stopped cooling yesterday, blower runs but no cold air"
                />
              </div>
              <div className="sm:col-span-2 flex items-start gap-3 p-4 rounded-xl bg-white border border-slate-200">
                <Checkbox
                  id="emergency"
                  data-testid="checkbox-emergency"
                  checked={form.is_emergency}
                  onCheckedChange={(c) => update("is_emergency", !!c)}
                  className="mt-0.5"
                />
                <Label htmlFor="emergency" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                  <span className="font-semibold text-slate-900">This is an emergency.</span>{" "}
                  We'll prioritize your request — for fastest response, please also call{" "}
                  <a href="tel:7852880330" className="text-[#E31837] font-semibold">785-288-0330</a>.
                </Label>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="submit-booking-btn"
              disabled={submitting}
              className="mt-6 w-full h-14 rounded-full bg-[#E31837] hover:bg-[#C8102E] text-white text-base font-semibold shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {submitting ? "Sending…" : (
                <>
                  Request Appointment <Send className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
            <p className="mt-3 text-xs text-slate-500 text-center">
              By submitting you agree to be contacted about your service request.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
